import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboard';
import { LiveNewsItem } from '../../types';
import logo from '../../logo.jpeg';
import papilioDemoleusImage from '../../assets/images/species/papilio-demoleus.jpg';

const NEWS_SOURCE_THUMBNAILS: Record<string, string> = {
  'mongabay india': 'https://www.google.com/s2/favicons?domain=india.mongabay.com&sz=128',
  'down to earth': 'https://www.google.com/s2/favicons?domain=downtoearth.org.in&sz=128',
  'the hindu - environment': 'https://www.google.com/s2/favicons?domain=thehindu.com&sz=128',
  'the hindu - agriculture': 'https://www.google.com/s2/favicons?domain=thehindu.com&sz=128',
  'the hindu - science': 'https://www.google.com/s2/favicons?domain=thehindu.com&sz=128',
  'nasa earth': 'https://www.google.com/s2/favicons?domain=nasa.gov&sz=128',
  mongabay: 'https://www.google.com/s2/favicons?domain=news.mongabay.com&sz=128',
  'the guardian environment': 'https://www.google.com/s2/favicons?domain=theguardian.com&sz=128',
  'sciencedaily - environment': 'https://www.google.com/s2/favicons?domain=sciencedaily.com&sz=128',
  'sciencedaily - climate': 'https://www.google.com/s2/favicons?domain=sciencedaily.com&sz=128',
  'sciencedaily - animals': 'https://www.google.com/s2/favicons?domain=sciencedaily.com&sz=128',
  'sciencedaily - endangered species': 'https://www.google.com/s2/favicons?domain=sciencedaily.com&sz=128',
  'phys.org - environment': 'https://www.google.com/s2/favicons?domain=phys.org&sz=128',
};

const FALLBACK_SOURCE_LOGOS = [
  'https://www.google.com/s2/favicons?domain=www.iucn.org&sz=128',
  'https://www.google.com/s2/favicons?domain=www.gbif.org&sz=128',
  'https://www.google.com/s2/favicons?domain=news.mongabay.com&sz=128',
  'https://www.google.com/s2/favicons?domain=www.nasa.gov&sz=128',
  'https://www.google.com/s2/favicons?domain=www.theguardian.com&sz=128',
  'https://www.google.com/s2/favicons?domain=www.sciencedaily.com&sz=128',
  'https://www.google.com/s2/favicons?domain=phys.org&sz=128',
];

const getStableIndex = (text: string, length: number): number => {
  if (!text || length <= 0) {
    return 0;
  }
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return hash % length;
};

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const trendBars = [18, 26, 24, 32, 37, 41, 46, 53, 58, 60, 67, 73];
  const [news, setNews] = useState<LiveNewsItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState('');
  const [newsFetchedAt, setNewsFetchedAt] = useState<string | null>(null);
  const [newsFallback, setNewsFallback] = useState(false);
  const [newsStartIndex, setNewsStartIndex] = useState(0);

  const fetchNews = useCallback(async () => {
    setNewsLoading(true);
    setNewsError('');
    try {
      const response = await dashboardService.getLiveNews(8);
      setNews(response.items || []);
      setNewsFetchedAt(response.fetched_at || null);
      setNewsFallback(Boolean(response.is_fallback));
    } catch (error: any) {
      setNews([]);
      setNewsError(error?.message || 'Unable to load live news right now.');
    } finally {
      setNewsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchNews();
  }, [fetchNews]);

  const newsUpdatedLabel = useMemo(() => {
    if (!newsFetchedAt) {
      return 'Not yet synced';
    }
    const parsed = new Date(newsFetchedAt);
    if (Number.isNaN(parsed.getTime())) {
      return 'Recently synced';
    }
    return parsed.toLocaleString();
  }, [newsFetchedAt]);

  const sortedNews = useMemo(() => {
    return [...news].sort((a, b) => {
      const aTime = new Date(a.published_at || 0).getTime();
      const bTime = new Date(b.published_at || 0).getTime();
      return bTime - aTime;
    });
  }, [news]);

  useEffect(() => {
    setNewsStartIndex(0);
  }, [sortedNews.length]);

  useEffect(() => {
    if (newsLoading || sortedNews.length <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      setNewsStartIndex((current) => (current + 1) % sortedNews.length);
    }, 4200);

    return () => {
      window.clearInterval(timer);
    };
  }, [newsLoading, sortedNews.length]);

  const visibleNews = useMemo(() => {
    if (!sortedNews.length) {
      return [];
    }
    const count = Math.min(4, sortedNews.length);
    return Array.from({ length: count }, (_, index) => sortedNews[(newsStartIndex + index) % sortedNews.length]);
  }, [sortedNews, newsStartIndex]);

  const isNewNewsItem = useCallback((publishedAt?: string | null) => {
    if (!publishedAt) {
      return false;
    }
    const publishedTime = new Date(publishedAt).getTime();
    if (Number.isNaN(publishedTime)) {
      return false;
    }
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - publishedTime <= sevenDaysMs;
  }, []);

  const formatNewsDate = useCallback((publishedAt?: string | null) => {
    if (!publishedAt) {
      return 'Date unavailable';
    }
    const parsed = new Date(publishedAt);
    if (Number.isNaN(parsed.getTime())) {
      return 'Date unavailable';
    }
    return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  }, []);

  const getNewsThumbnail = useCallback((item: LiveNewsItem) => {
    const source = (item.source || '').trim().toLowerCase();
    if (NEWS_SOURCE_THUMBNAILS[source]) {
      return NEWS_SOURCE_THUMBNAILS[source];
    }

    const bestMatch = Object.keys(NEWS_SOURCE_THUMBNAILS).find((name) => source.includes(name));
    if (bestMatch) {
      return NEWS_SOURCE_THUMBNAILS[bestMatch];
    }

    const stableFallback = getStableIndex(source || 'default', FALLBACK_SOURCE_LOGOS.length);
    return FALLBACK_SOURCE_LOGOS[stableFallback];
  }, []);

  const goToNextNews = useCallback(() => {
    if (!sortedNews.length) {
      return;
    }
    setNewsStartIndex((current) => (current + 1) % sortedNews.length);
  }, [sortedNews.length]);

  const goToPreviousNews = useCallback(() => {
    if (!sortedNews.length) {
      return;
    }
    setNewsStartIndex((current) => (current - 1 + sortedNews.length) % sortedNews.length);
  }, [sortedNews.length]);

  return (
    <section className="home-page" aria-labelledby="home-title">
      <div className="home-layout">
        <section className="home-hero" aria-label="EcoPredict hero section">
          <div className="home-hero-overlay" />
          <div className="home-hero-content">
            <div className="home-brand-row">
              <img src={logo} alt="EcoPredict" className="home-logo" />
              <p className="home-kicker">EcoPredict</p>
            </div>

            <h1 id="home-title">AI-Driven Biodiversity Forecasting</h1>
            <p className="home-subtitle">
              Track and predict insect biodiversity loss with data-powered ecological intelligence.
            </p>

            <div className="home-hero-stats" aria-label="Platform highlights">
              <span><strong>Live Feed:</strong> Biodiversity + climate headlines</span>
              <span><strong>Coverage:</strong> Global insect risk patterns</span>
              <span><strong>Mode:</strong> Research-grade monitoring</span>
            </div>

            <div className="home-actions" role="group" aria-label="Primary home actions">
              <Link to="/dashboard" className="home-primary-cta">
                Explore Dashboard
              </Link>
              {!isAuthenticated && (
                <Link to="/login" className="home-secondary-cta">
                  Sign in
                </Link>
              )}
            </div>

            <div className="home-hero-badges" aria-label="Platform value badges">
              <span>Forecast-first platform</span>
              <span>Global biodiversity context</span>
              <span>Live environmental headlines</span>
            </div>
          </div>

          <div className="home-command-row" aria-label="Operational summary">
            <article>
              <p>Model Confidence</p>
              <h3>High</h3>
            </article>
            <article>
              <p>Historical Scope</p>
              <h3>1990-2026+</h3>
            </article>
            <article>
              <p>Operating Mode</p>
              <h3>Real-time</h3>
            </article>
          </div>
        </section>

        <section className="home-about-ecopredict" aria-labelledby="home-about-ecopredict-title">
          <h2 id="home-about-ecopredict-title">What is EcoPredict?</h2>

          <div className="home-about-ecopredict-grid">
            <div className="home-about-visual" aria-hidden="true">
              <div className="home-about-orbit" />
              <img
                src={papilioDemoleusImage}
                alt="Papilio demoleus butterfly for biodiversity conservation, insect habitat monitoring, and species decline awareness"
              />
            </div>

            <article className="home-about-copy">
              <p>
                EcoPredict is an AI-powered biodiversity intelligence platform that combines climate,
                habitat, and species-occurrence signals to forecast ecosystem stress early. It helps
                researchers, conservation teams, and decision-makers monitor decline trends and act
                sooner with data-backed risk insights.
              </p>

              <Link to="/about" className="home-about-link">
                Learn more
              </Link>
            </article>
          </div>
        </section>

        <section className="home-insights" aria-label="Biodiversity analytics snapshot">
          <article className="home-story-card">
            <p className="home-section-kicker">Biodiversity Loss</p>
            <h2>Precision Risk Intelligence</h2>
            <p>
              Discover species decline trends, identify high-risk zones, and respond early with evidence-backed forecasts.
            </p>
            <div className="home-mini-metrics">
              <p><strong>24/7</strong> model-backed risk visibility</p>
              <p><strong>Multi-source</strong> environment + occurrence context</p>
              <p><strong>Fast triage</strong> for response planning</p>
            </div>
            <div className="home-story-actions">
              <Link to="/dashboard" className="home-dashboard-link">
                Explore Dashboard
              </Link>
              {!isAuthenticated && (
                <Link to="/signup" className="auth-link-btn">
                  Create account
                </Link>
              )}
            </div>
          </article>

          <article className="home-analytics-card">
            <div className="home-analytics-header">
              <p className="home-section-kicker">Score Trends</p>
              <h3>Moderate</h3>
            </div>

            <div className="home-trend-chart" aria-hidden="true">
              {trendBars.map((height, index) => (
                <span key={`${height}-${index}`} style={{ height: `${height}px` }} />
              ))}
            </div>

            <div className="home-score-ring" aria-label="Current biodiversity score">
              <div className="home-score-ring-inner">
                <strong>68.5%</strong>
                <span>Decline</span>
              </div>
            </div>
          </article>
        </section>

        <section className="home-news-panel" aria-label="Live biodiversity news">
          <div className="home-news-header">
            <div>
              <p className="home-section-kicker">Live News</p>
              <h2>Species and Environmental Headlines</h2>
            </div>
            <div className="home-news-toolbar">
              <button type="button" className="home-news-nav" onClick={goToPreviousNews} disabled={sortedNews.length <= 1 || newsLoading} aria-label="Previous news">
                &larr;
              </button>
              <button type="button" className="home-news-nav" onClick={goToNextNews} disabled={sortedNews.length <= 1 || newsLoading} aria-label="Next news">
                &rarr;
              </button>
              <button type="button" className="home-news-refresh" onClick={() => { void fetchNews(); }} disabled={newsLoading}>
                {newsLoading ? 'Syncing...' : 'Refresh'}
              </button>
            </div>
          </div>

          <p className="home-news-meta">
            Updated: {newsUpdatedLabel}{newsFallback ? ' · fallback feed' : ''}
          </p>

          {newsError && <p className="form-error">{newsError}</p>}

          {newsLoading ? (
            <div className="home-news-skeleton" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          ) : (
            <div className="home-news-carousel" aria-label="Rotating headlines">
              {visibleNews.map((item, index) => {
                const isFresh = isNewNewsItem(item.published_at);
                const tagLabel = isFresh ? 'New' : 'News';
                return (
                <a
                  key={`${item.url}-${item.published_at || ''}-${newsStartIndex}-${index}`}
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`home-news-card ${isFresh ? 'is-fresh' : ''}`}
                >
                  <div className="home-news-thumb">
                    <img src={getNewsThumbnail(item)} alt="" aria-hidden="true" className="home-news-thumb-image" />
                    <span className="home-news-tag">{tagLabel}</span>
                  </div>
                  <div className="home-news-card-body">
                    <p className="home-news-source">{item.source}</p>
                    <h3>{item.title}</h3>
                    <p>{item.summary || 'Open this article for full details.'}</p>
                    <div className="home-news-card-footer">
                      <span className="home-news-date">{formatNewsDate(item.published_at)}</span>
                      <span className="home-news-link">Open</span>
                    </div>
                  </div>
                </a>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </section>
  );
};

export default HomePage;
